import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  useStudyGroupResults,
  useStudyGroup,
  useCreateStudyGroup,
} from '../lib/api';
import { motion, springs, Pressable, FadeIn, Stagger, PageTransition, CountUp, Confetti, Skeleton } from '../lib/motion';
import { haptic } from '../lib/haptics';

export function StudyGroupResultsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isLoading } = useStudyGroupResults(id!);
  const { data: groupData } = useStudyGroup(id!);
  const playAgain = useCreateStudyGroup();

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const handlePlayAgain = async () => {
    if (!groupData?.group) return;
    const g = groupData.group;
    try {
      const result = await playAgain.mutateAsync({
        sourceType: g.source_type,
        sourceId: g.source_id,
        topicFilter: g.topic_filter ?? null,
        questionCount: g.question_count,
        timerSeconds: g.timer_seconds,
      });
      navigate(`/study-groups/${result.group.id}/lobby`);
    } catch { /* error handled */ }
  };

  const standings = data?.standings ?? [];
  const winner = standings.find((p) => p.rank === 1);
  const rest = standings.filter((p) => p !== winner);

  // One celebratory buzz when the final standings land (winner moment).
  useEffect(() => {
    if (!winner) return;
    haptic.success();
  }, [winner?.userId]);

  if (isLoading || !data) {
    return (
      <div className="max-w-lg mx-auto pb-24 px-4 pt-1 space-y-4">
        <Skeleton className="h-8 w-32" />
        <Skeleton className="h-40 w-full rounded-3xl" />
        <Skeleton className="h-52 w-full rounded-2xl" />
        <Skeleton className="h-14 w-full rounded-2xl" />
      </div>
    );
  }

  return (
    <PageTransition className="relative max-w-lg mx-auto pb-24 px-4 space-y-5">
      {/* One tasteful confetti burst over the winner reveal. */}
      {winner && <Confetti count={36} duration={1.8} />}

      {/* ── Header ────────────────────────────────────────────── */}
      <div className="flex items-center gap-3">
        <Pressable
          onClick={() => navigate('/study-groups')}
          className="flex h-10 w-10 -ml-2 items-center justify-center rounded-full text-slate-500 hover:text-squishy-dark transition-colors"
          aria-label="Back"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </Pressable>
        <div>
          <h1 className="text-xl font-bold font-display text-squishy-dark">Results</h1>
          <p className="text-xs text-slate-500">
            {groupData?.group?.title ?? ''} · {data.totalQuestions} questions
          </p>
        </div>
      </div>

      {/* ── Winner spotlight ──────────────────────────────────── */}
      {winner && (
        <motion.div
          initial={{ opacity: 0, scale: 0.92, y: 12 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ ...springs.bouncy, delay: 0.1 }}
          className="relative overflow-hidden bg-gradient-to-br from-squishy-navy to-squishy-navySoft rounded-3xl p-6 text-center shadow-pop"
        >
          <div className="pointer-events-none absolute -top-10 left-1/2 h-32 w-64 -translate-x-1/2 rounded-full bg-squishy-gold/20 blur-2xl" aria-hidden />
          <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-squishy-gold text-squishy-navy shadow-soft">
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M8 21h8m-4-4v4m-5.5-17h11v5.5a5.5 5.5 0 01-11 0V4zM6.5 6H4a1 1 0 00-1 1c0 2.5 1.5 4.5 3.5 4.9M17.5 6H20a1 1 0 011 1c0 2.5-1.5 4.5-3.5 4.9" />
            </svg>
          </div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.25em] text-squishy-gold">Winner</div>
          <div className="mt-1 text-xl font-bold font-display text-white truncate">{winner.displayName}</div>
          <div className="mt-1 text-3xl font-black font-display text-white tabular-nums">
            <ScoreCount value={winner.totalPoints} /><span className="ml-1 text-sm font-semibold text-white/60">pts</span>
          </div>
          <div className="mt-1 text-xs text-white/60">
            {winner.accuracy}% accuracy · {winner.correctCount}/{data.totalQuestions} correct
            {winner.longestStreak > 1 ? ` · ${winner.longestStreak} streak` : ''}
          </div>
        </motion.div>
      )}

      {/* ── Leaderboard ───────────────────────────────────────── */}
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-card">
        <div className="px-4 py-3 border-b border-slate-100">
          <h2 className="text-sm font-semibold text-squishy-dark">Final Standings</h2>
        </div>
        <Stagger className="divide-y divide-slate-100">
          {(winner ? rest : standings).map((p) => (
            <Stagger.Item key={p.userId}>
              <div
                className={`flex items-center gap-3 px-4 py-3 ${p.status === 'left' ? 'opacity-60' : ''}`}
              >
                {/* Rank */}
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                  p.rank === 1 ? 'bg-squishy-gold text-squishy-navy' :
                  p.rank === 2 ? 'bg-slate-300 text-slate-700' :
                  p.rank === 3 ? 'bg-amber-600/20 text-amber-700' :
                  'bg-slate-100 text-slate-500'
                }`}>
                  {p.rank}
                </div>

                {/* Avatar + name */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-slate-800 truncate">
                      {p.displayName}
                    </span>
                    {p.status === 'left' && (
                      <span className="text-xs text-slate-400">(disconnected)</span>
                    )}
                  </div>
                  <div className="text-xs text-slate-400">
                    {p.accuracy}% accuracy
                    {p.avgResponseMs ? ` · ${(p.avgResponseMs / 1000).toFixed(1)}s avg` : ''}
                    {p.longestStreak > 1 ? ` · ${p.longestStreak} streak` : ''}
                  </div>
                </div>

                {/* Points */}
                <div className="text-right flex-shrink-0">
                  <div className="text-sm font-bold text-squishy-dark tabular-nums"><ScoreCount value={p.totalPoints} /></div>
                  <div className="text-xs text-slate-400">
                    {p.correctCount}/{data.totalQuestions}
                  </div>
                </div>
              </div>
            </Stagger.Item>
          ))}
        </Stagger>
      </div>

      {/* ── MVP Awards ────────────────────────────────────────── */}
      {data.awards && Object.keys(data.awards).length > 0 && (
        <div className="space-y-2">
          <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Awards</h2>
          <Stagger className="grid grid-cols-2 gap-2">
            {data.awards.fastestFingers && (
              <Stagger.Item>
                <AwardCard
                  icon="⚡"
                  label="Fastest Fingers"
                  name={data.awards.fastestFingers.displayName}
                  value={`${(data.awards.fastestFingers.avgMs / 1000).toFixed(1)}s avg`}
                />
              </Stagger.Item>
            )}
            {data.awards.streakKing && (
              <Stagger.Item>
                <AwardCard
                  icon="🔥"
                  label="Streak King"
                  name={data.awards.streakKing.displayName}
                  value={`${data.awards.streakKing.streak} in a row`}
                />
              </Stagger.Item>
            )}
            {data.awards.clutchPlayer && (
              <Stagger.Item>
                <AwardCard
                  icon="🎯"
                  label="Clutch Player"
                  name={data.awards.clutchPlayer.displayName}
                  value={`${data.awards.clutchPlayer.clutchPoints} pts (last 5)`}
                />
              </Stagger.Item>
            )}
            {data.awards.perfectScore && Array.isArray(data.awards.perfectScore) && (
              data.awards.perfectScore.map((p: any) => (
                <Stagger.Item key={p.userId}>
                  <AwardCard
                    icon="🏆"
                    label="Perfect Score"
                    name={p.displayName}
                    value="100%"
                  />
                </Stagger.Item>
              ))
            )}
          </Stagger>
        </div>
      )}

      {/* ── Topic breakdown ───────────────────────────────────── */}
      {data.topicBreakdown.length > 0 && (
        <FadeIn>
          <div className="bg-white border border-slate-200 rounded-2xl p-4 space-y-3 shadow-card">
            <h2 className="text-sm font-semibold text-squishy-dark">Topic Breakdown</h2>
            <div className="space-y-3">
              {data.topicBreakdown.map((t) => (
                <div key={t.topic}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-slate-700 font-medium">{t.topic}</span>
                    <span className="text-xs text-slate-400">
                      You: {t.userAccuracy}% / Group: {t.groupAccuracy}%
                    </span>
                  </div>
                  <div className="flex gap-1 h-2">
                    <div className="flex-1 bg-slate-100 rounded-full overflow-hidden">
                      <motion.div
                        className="h-full bg-squishy-primary rounded-full"
                        initial={{ width: 0 }}
                        animate={{ width: `${t.userAccuracy}%` }}
                        transition={{ ...springs.soft, delay: 0.2 }}
                      />
                    </div>
                    <div className="flex-1 bg-slate-100 rounded-full overflow-hidden">
                      <motion.div
                        className="h-full bg-slate-400 rounded-full"
                        initial={{ width: 0 }}
                        animate={{ width: `${t.groupAccuracy}%` }}
                        transition={{ ...springs.soft, delay: 0.3 }}
                      />
                    </div>
                  </div>
                  <div className="flex gap-1 mt-0.5">
                    <span className="flex-1 text-[10px] text-squishy-primary">You</span>
                    <span className="flex-1 text-[10px] text-slate-400">Group</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </FadeIn>
      )}

      {/* ── Action buttons ────────────────────────────────────── */}
      <div className="flex gap-3">
        <Pressable
          onClick={() => navigate(`/study-groups/${id}/review`)}
          className="flex-1 bg-white border border-slate-200 text-squishy-dark font-semibold py-3.5 rounded-2xl shadow-card hover:border-squishy-teal/60 transition-colors text-sm"
        >
          Review Questions
        </Pressable>
        <Pressable
          haptic="medium"
          onClick={handlePlayAgain}
          disabled={playAgain.isPending}
          className="flex-1 bg-squishy-primary text-white font-semibold py-3.5 rounded-2xl disabled:opacity-50 shadow-soft transition-opacity text-sm"
        >
          {playAgain.isPending ? 'Creating...' : 'Play Again'}
        </Pressable>
      </div>
    </PageTransition>
  );
}

// CountUp animates on change, not on mount; start at 0 so scores visibly roll up.
function ScoreCount({ value, className }: { value: number; className?: string }) {
  const [v, setV] = useState(0);
  useEffect(() => {
    const t = setTimeout(() => setV(value), 250);
    return () => clearTimeout(t);
  }, [value]);
  return <CountUp value={v} className={className} />;
}

function AwardCard({ icon, label, name, value }: { icon: string; label: string; name: string; value: string }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-3.5 text-center shadow-card">
      <div className="mx-auto mb-1.5 flex h-9 w-9 items-center justify-center rounded-full bg-squishy-gold/15 text-base">{icon}</div>
      <div className="text-xs font-bold text-squishy-dark">{label}</div>
      <div className="text-xs text-slate-500 mt-0.5 truncate">{name}</div>
      <div className="text-xs text-squishy-primary font-semibold mt-0.5">{value}</div>
    </div>
  );
}
